import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

/**
 * Public page reached from the "Verify my email" button in the
 * verification email. Reads the `token` query param, hits the backend
 * verify endpoint, and shows a success/failure state.
 */
export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [state, setState] = useState('loading'); // 'loading' | 'ok' | 'error'
  const [message, setMessage] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');

  useEffect(() => {
    if (!token) {
      setState('error');
      setMessage('This verification link is missing its token. Please open the link from your email directly.');
      return;
    }
    axios.get(`${API}/auth/verify-email`, { params: { token } })
      .then(r => {
        setState('ok');
        setMessage(r.data?.message || 'Email verified.');
        setVerifiedEmail(r.data?.email || '');
      })
      .catch(e => {
        setState('error');
        setMessage(e.response?.data?.detail || 'Could not verify this email link.');
      });
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#FAF9F6' }}>
      <Card className="w-full max-w-md p-8 shadow-lg text-center" data-testid="verify-email-page">
        {state === 'loading' && (
          <>
            <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin" style={{ color: '#2D4A3E' }} />
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
              Verifying your email…
            </h1>
          </>
        )}
        {state === 'ok' && (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto mb-4 text-green-600" />
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
              Email verified
            </h1>
            {verifiedEmail && (
              <p className="text-sm text-muted-foreground mb-2">{verifiedEmail}</p>
            )}
            <p className="text-muted-foreground mb-6">{message}</p>
            <Link
              to="/login"
              className="inline-block px-6 py-3 rounded-full font-medium"
              style={{ backgroundColor: '#2D4A3E', color: 'white' }}
              data-testid="verify-email-signin-btn"
            >
              Sign in
            </Link>
          </>
        )}
        {state === 'error' && (
          <>
            <XCircle className="h-14 w-14 mx-auto mb-4 text-red-600" />
            <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
              Verification failed
            </h1>
            <p className="text-muted-foreground mb-6">{message}</p>
            <Link to="/login" className="font-medium" style={{ color: '#2D4A3E' }}>
              Back to sign-in
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}
